<script>
  import { createEventDispatcher } from "svelte";
  import {
    Popup,
    Page,
    Navbar,
    NavRight,
    Link,
    Block,
    Card,
    CardHeader,
    CardContent,
    Button,
    Badge,
    Icon,
    f7,
  } from "framework7-svelte";
  import UserSearchTypeahead from "./UserSearchTypeahead.svelte";
  import { TradingService } from "../services/tradingService";

  export let opened = false;
  export let user = null;
  export let users = [];
  export let lockUser = false;
  export let initialTag = "";
  export let initialMultiplier = 1.0;
  export let initialMasterOptIn = false;
  export let showMultiplier = true;
  export let environment = "LIVE";

  const dispatch = createEventDispatcher();

  let selectedUser = null;
  let tagInput = "";
  let multiplier = 1.0;
  let masterOptIn = false;
  let isSaving = false;
  let searchQuery = "";

  $: if (opened) {
    selectedUser = user || null;
    tagInput = initialTag || (user ? user.client_tag || user.ucc || "" : "");
    multiplier = initialMultiplier || 1.0;
    masterOptIn = initialMasterOptIn || false;
    searchQuery = user ? `${user.fullName} (${user.mobile})` : "";
  }

  function handleUserSelect(e) {
    selectedUser = e.detail;
    tagInput = selectedUser.client_tag || selectedUser.ucc || "";
  }

  function clearSelectedUser() {
    if (lockUser) return;
    selectedUser = null;
    searchQuery = "";
    tagInput = "";
  }

  async function submitAssign() {
    const tag = (tagInput || "").trim().toUpperCase();
    if (!selectedUser) {
      f7.dialog.alert("Please select a user account.", "User Required");
      return;
    }
    if (!tag) {
      f7.dialog.alert("Please enter a valid UCC Tag.", "UCC Tag Required");
      return;
    }

    isSaving = true;
    try {
      const payload = {
        user_id: selectedUser.user_id || selectedUser._id,
        mobile: selectedUser.mobile,
        client_tag: tag,
        ucc: tag,
        multiplier: multiplier,
        master_opt_in: masterOptIn,
      };

      await TradingService.assignUccTag(payload, false);

      f7.toast
        .create({
          text: `✓ UCC Tag ${tag} assigned to ${selectedUser.fullName}!`,
          closeTimeout: 3000,
        })
        .open();

      dispatch("success", {
        user: selectedUser,
        tag: tag,
        multiplier: multiplier,
        master_opt_in: masterOptIn,
      });

      opened = false;
    } catch (err) {
      console.error("Assign UCC tag error:", err);
      f7.dialog.alert(err.message || "Failed to assign UCC Tag", "Error");
    } finally {
      isSaving = false;
    }
  }

  function confirmUnassign() {
    if (!selectedUser) return;
    const currentTag = initialTag || (selectedUser ? selectedUser.client_tag || selectedUser.ucc : "");
    f7.dialog.confirm(
      `Are you sure you want to revoke/clear UCC Tag "${currentTag}" for ${selectedUser.fullName || selectedUser.mobile}? This account will be unassigned and barred from auto-trading.`,
      "Revoke / Clear UCC Tag",
      () => {
        submitUnassign();
      }
    );
  }

  async function submitUnassign() {
    if (!selectedUser) return;
    isSaving = true;
    try {
      const res = await fetch(`${ENGINE_URL}/client-tag/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: selectedUser.user_id || selectedUser._id,
          mobile: selectedUser.mobile,
          action: "unassign",
          client_tag: "",
          ucc: "",
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      f7.toast
        .create({
          text: `✓ UCC Tag cleared from ${selectedUser.fullName || selectedUser.mobile}!`,
          closeTimeout: 3000,
        })
        .open();

      dispatch("success", {
        user: selectedUser,
        tag: "",
        action: "unassign",
      });

      opened = false;
    } catch (err) {
      console.error("Unassign UCC tag error:", err);
      f7.dialog.alert(err.message || "Failed to clear UCC Tag", "Error");
    } finally {
      isSaving = false;
    }
  }
</script>

<Popup {opened} onPopupClosed={() => (opened = false)} class="assign-ucc-modal">
  <Page>
    <Navbar title={selectedUser ? (lockUser ? "Assign / Manage UCC Tag" : "Assign UCC Tag to User") : "Assign UCC Tag to User"}>
      <NavRight>
        <Link popupClose>Close</Link>
      </NavRight>
    </Navbar>
    <Block>
      <Card>
        <CardHeader>
          <div>
            <strong>Link / Manage UCC Tag</strong>
            <div style="font-size: 12px; color: #64748b; margin-top: 2px;">
              Assign or revoke execution tags tied to MongoDB accounts & engine RMS
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <!-- User Selection Section -->
          {#if selectedUser}
            <div class="user-card-locked margin-bottom">
              <div class="user-card-header">
                <div class="user-avatar">
                  {(selectedUser.fullName || "U").charAt(0).toUpperCase()}
                </div>
                <div style="flex: 1; min-width: 0;">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <span class="user-name">{selectedUser.fullName || "-"}</span>
                    <Badge color={selectedUser.account_status === "approved" ? "green" : "orange"}>
                      {selectedUser.account_status || "pending"}
                    </Badge>
                  </div>
                  <div class="user-subtext">
                    📱 <strong>{selectedUser.mobile || "-"}</strong> &nbsp;|&nbsp; PAN: <strong>{selectedUser.pan || "N/A"}</strong>
                  </div>
                  {#if selectedUser.emailAddress}
                    <div class="user-subtext">✉️ {selectedUser.emailAddress}</div>
                  {/if}
                </div>

                {#if !lockUser}
                  <Button small outline color="gray" onClick={clearSelectedUser} tooltip="Change User">
                    Change
                  </Button>
                {/if}
              </div>
            </div>
          {:else}
            <!-- Search Typeahead (when user is not locked) -->
            <div class="form-group margin-bottom">
              <label class="form-label" for="assignUserSearch">Select User (Name, Mobile, PAN, or UCC):</label>
              <UserSearchTypeahead
                id="assignUserSearch"
                {users}
                placeholder="Search user to link (e.g. Rahul, 9876543210)..."
                filterUntaggedOnly={true}
                bind:value={searchQuery}
                on:select={handleUserSelect}
              />
            </div>
          {/if}

          <!-- UCC Tag Input -->
          <div class="form-group margin-bottom">
            <label class="form-label" for="uccTagInput">UCC Tag:</label>
            <input
              id="uccTagInput"
              type="text"
              class="custom-input font-mono"
              placeholder="e.g. TRDR_001, FEDE_01"
              bind:value={tagInput}
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              style="text-transform: uppercase;"
            />
            <div style="font-size: 11px; color: #64748b; margin-top: 4px;">
              This tag identifies this user in the BSE BOW execution engine. Leave empty or click Revoke to clear.
            </div>
          </div>

          <div style="display: flex; gap: 10px; margin-top: 16px;">
            {#if initialTag || (selectedUser && (selectedUser.client_tag || selectedUser.ucc))}
              <Button
                outline
                large
                color="red"
                onClick={confirmUnassign}
                disabled={isSaving || !selectedUser}
                style="flex: 1;"
              >
                🚫 Revoke UCC Tag
              </Button>
            {/if}
            <Button
              fill
              large
              color="blue"
              onClick={submitAssign}
              disabled={isSaving || !selectedUser || !tagInput.trim()}
              style="flex: 2;"
            >
              {isSaving ? "Saving..." : "✓ Save UCC Tag"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </Block>
  </Page>
</Popup>

<style>
  .user-card-locked {
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 16px;
  }

  .user-card-header {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #e0e7ff;
    color: #3730a3;
    font-weight: 700;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .user-name {
    font-weight: 700;
    font-size: 15px;
    color: #0f172a;
  }

  .user-subtext {
    font-size: 12px;
    color: #475569;
    margin-top: 2px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 6px;
    text-transform: uppercase;
  }

  .custom-input {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 14px;
    font-size: 14px;
    border: 1.5px solid #cbd5e1;
    border-radius: 8px;
    outline: none;
    background: #ffffff;
    color: #0f172a;
    transition: all 0.2s ease;
  }

  .custom-input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }

  .font-mono {
    font-family: monospace;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
</style>
